import type { SupSingleVaultV3, SupStoredFilterRangeValue } from "@/types";
import React, { useCallback, useMemo } from "react";

import { formatCurrencyCompact } from "@/utils";
import dictionary from "@/../config/dictionary.config";
import { useStoredData, useStoredDataDispatch } from "@/store";

import GridBarFiltersAtomRange from "@/components/Grid/Bar/Filters/Atom/Range";
import GridBarFiltersAtomHeader from "@/components/Grid/Bar/Filters/Atom/Header";

interface GridBarFiltersTvlRangeProps {
  vaults?: SupSingleVaultV3[];
}

export default function GridBarFiltersTvlRange({ vaults }: GridBarFiltersTvlRangeProps) {
  // edge case with super-big tvl of a single vault (e.g. 1 billion) breaking the whole slider range
  const superBigMaxValue = useMemo(() => {
    const [firstBiggest, secondBiggest] = (vaults ?? [])
      .filter(vault => vault.vault_statistics.displayTvl !== dictionary.statsDefaultValue)
      .sort((x, y) => y.vault_statistics.displayTvl - x.vault_statistics.displayTvl)
      .slice(0, 2);
    if (!firstBiggest || !secondBiggest) {
      return undefined;
    }

    if (firstBiggest.vault_statistics.displayTvl / secondBiggest.vault_statistics.displayTvl > 3) {
      return firstBiggest.vault_statistics.displayTvl;
    }
    return undefined;
  }, [vaults]);

  const { min, max } = useMemo(() => {
    const filteredVaults = superBigMaxValue !== undefined ? vaults?.filter(vault => vault.vault_statistics.displayTvl < superBigMaxValue) : vaults;
    return (
      filteredVaults
        ?.filter(vault => vault.vault_statistics.displayTvl !== dictionary.statsDefaultValue)
        .reduce(
          (acc, vault) => {
            return {
              min: Math.min(acc.min, vault.vault_statistics.displayTvl),
              max: Math.max(acc.max, vault.vault_statistics.displayTvl),
            };
          },
          { min: Infinity, max: -Infinity }
        ) ?? { min: 0, max: 0 }
    );
  }, [superBigMaxValue, vaults]);

  const displayComputedValue = useCallback(
    (value: number) => (superBigMaxValue !== undefined && Math.floor(max) - 10 < Math.floor(value) && Math.floor(value) <= Math.floor(max) + 10 ? superBigMaxValue : value),
    [max, superBigMaxValue]
  );
  const storedData = useStoredData();

  const storedDispatch = useStoredDataDispatch();

  const filterValue = useMemo(() => {
    const filterValues = storedData.filters.find(storedFilter => storedFilter.name === "tvl");
    return (filterValues?.values ?? { min, max }) as SupStoredFilterRangeValue;
  }, [min, max, storedData.filters]);

  const handleFilterChange = useCallback(
    (from: number, to: number) => {
      storedDispatch({
        type: "setFilter",
        filter: {
          name: "tvl",
          values: {
            min: from,
            max: displayComputedValue(to),
          },
        },
      });
    },
    [displayComputedValue, storedDispatch]
  );

  const formatter = useCallback(
    (value: number) => {
      const processedValue = displayComputedValue(value);
      return formatCurrencyCompact(processedValue);
    },
    [displayComputedValue]
  );

  return (
    <GridBarFiltersAtomHeader
      name="tvl"
      title="TVL Range"
      hideCount
      caption={`${formatCurrencyCompact(filterValue.min)}-${formatCurrencyCompact(displayComputedValue(filterValue.max))}`}
    >
      <GridBarFiltersAtomRange fieldType="dollar" step={1} max={max} from={filterValue.min} to={filterValue.max} min={min} onChange={handleFilterChange} formatter={formatter} />
    </GridBarFiltersAtomHeader>
  );
}
